import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import { ClearFiltersHint } from '~components/Filters/ClearFiltersHint';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { FetchReason } from '~constants/enums';
import { Job } from '~hooks/useJob/models';
import { useLiveMapNew } from '~hooks/useLiveMap/useLiveMapNew';
import { Order } from '~hooks/useOrders/models';
import { useStores } from '~store';
import useInfiniteScroll from '~utils/hooks/useInfiniteScroll';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import OrderCard from './OrderListComponents/OrderCard';

const ordersFetchInitialState = {
  initialFetchComplete: false,
  reason: FetchReason.INITIAL_FETCH,
  shouldFetch: false,
};

interface OrderListProps {
  isSidebarCollapsed?: boolean;
  onSelectJob: (job: Job) => void;
  onSelectOrder: (order?: Order) => void;
  onViewJobRoute: (job: Job) => void;
  selectedJob?: Job;
  selectedOrder?: Order;
  sx?: SxProps;
}

const OrderList = observer(
  ({
    isSidebarCollapsed,
    onSelectJob,
    onSelectOrder,
    onViewJobRoute,
    selectedJob,
    selectedOrder,
  }: OrderListProps) => {
    const theme = useTheme();
    const { liveMapStoreNew, userStore } = useStores();
    const { getCompanyOrdersByCompanyId, subscribeToJobsRTU, subscribeToOrdersRTU } =
      useLiveMapNew();

    const [ordersFetchState, setOrdersFetchState] = useState({
      ...ordersFetchInitialState,
    });

    const infiniteScroll = useInfiniteScroll({
      threshold: 0,
      root: document.querySelector('[data-infinite-scroll-loading-anchor]'),
      rootMargin: '1000px',
    });
    const orders = liveMapStoreNew.orders;
    const companyId = getEffectiveUserCompanyId(userStore);
    const areRequiredFiltersSet = Boolean(liveMapStoreNew.filters.jobStates);

    useEffect(() => {
      let ordersRTUSubscription: ReturnType<typeof subscribeToOrdersRTU> | null = null;
      let jobsRTUSubscription: ReturnType<typeof subscribeToJobsRTU> | null = null;

      if (companyId) {
        ordersRTUSubscription = subscribeToOrdersRTU(companyId);
        jobsRTUSubscription = subscribeToJobsRTU(companyId);
      }

      return () => {
        ordersRTUSubscription?.unsubscribe?.();
        jobsRTUSubscription?.unsubscribe?.();
      };
    }, [companyId]);

    useEffect(() => {
      if (areRequiredFiltersSet) {
        setOrdersFetchState({
          shouldFetch: true,
          reason: FetchReason.FILTERS_CHANGED,
          initialFetchComplete: false,
        });
      }
    }, [areRequiredFiltersSet, JSON.stringify(liveMapStoreNew.filters), companyId]);

    useEffect(() => {
      if (!ordersFetchState.shouldFetch) {
        return;
      }

      const fetchOrders = async () => {
        try {
          await getCompanyOrdersByCompanyId({ companyId });
        } catch {
          // Error is already handled in the hook
        } finally {
          infiniteScroll.notify();
          setOrdersFetchState((state) => ({
            ...state,
            shouldFetch: false,
            initialFetchComplete: true,
          }));
        }
      };

      void fetchOrders();
    }, [ordersFetchState.shouldFetch, ordersFetchState.reason]);

    useEffect(() => {
      setOrdersFetchState((state) => {
        if (liveMapStoreNew.isLoadingOrders || !areRequiredFiltersSet) {
          return { ...state, shouldFetch: false };
        }

        if (!state.initialFetchComplete) {
          return { ...state, shouldFetch: true, reason: FetchReason.INITIAL_FETCH };
        }

        if (infiniteScroll.endReached && liveMapStoreNew.hasMore) {
          return { ...state, shouldFetch: true, reason: FetchReason.INFINITE_SCROLL };
        }

        return { ...state, shouldFetch: false };
      });
    }, [
      liveMapStoreNew.isLoadingOrders,
      areRequiredFiltersSet,
      infiniteScroll.endReached,
      liveMapStoreNew.hasMore,
    ]);

    const handleSelectOrder = (order: Order, isSelected: boolean) => {
      if (isSelected) {
        onSelectOrder(undefined);
      } else {
        onSelectOrder(order);
      }
    };

    return (
      <Box
        data-infinite-scroll-loading-anchor
        sx={{
          p: 1,
          pb: 24,
          gap: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'auto',
          bgcolor: theme.brandV2.colors.treadGray10,
          visibility: isSidebarCollapsed ? 'hidden' : 'visible',
        }}
      >
        <LoadingSpinner
          isVisible={orders.length === 0 && liveMapStoreNew.isLoadingOrders}
        />
        {orders.map((order) => (
          <OrderCard
            key={order.orderId}
            order={order}
            onSelectJob={onSelectJob}
            onSelectOrder={handleSelectOrder}
            onViewJobRoute={onViewJobRoute}
            selectedJob={selectedJob}
            selectedOrder={selectedOrder}
          />
        ))}
        {liveMapStoreNew.hasMore ? (
          <Box ref={infiniteScroll.nodeRef}>
            <LoadingSpinner isVisible={liveMapStoreNew.isLoadingOrders} />
          </Box>
        ) : liveMapStoreNew.isLoadingOrders ? null : (
          <DividerWithText
            sx={{ mt: 0 }}
            text={
              liveMapStoreNew.orders.length === 0
                ? t('common.no_data')
                : t('dispatch.dispatch_v2.all_orders_loaded')
            }
          />
        )}

        {liveMapStoreNew.orders.length === 0 &&
          !liveMapStoreNew.isLoadingOrders &&
          liveMapStoreNew.hasUnfilteredResults && (
            <ClearFiltersHint
              onClearFilters={() => liveMapStoreNew.clearAllFiltersExceptSearch()}
            />
          )}
      </Box>
    );
  },
);

export default OrderList;
